import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import { NameIdObj } from 'common/models/GenericTypes';
import MultipleUserPicker from 'components/Pickers/MultipleUserPicker';
import RWTextFieldComponent from 'components/RWTextFieldComponent';
import React, { useCallback, useEffect, useState } from 'react';
import { CopySavedSearch } from 'services/SavedSearchesService';
import { GetMyUser } from 'services/UsersService';

interface Props {
  open: boolean;
  ssid: number;
  name: string;
  closeHandler: () => void;
  loadingHandler?: (isLoading: boolean) => void;
  successHandler?: (message: string, copiedToCurrentUser: boolean) => void;
  errorHandler?: (message: string) => void;
}

export default function CopySavedSearchDialog({
  open,
  ssid,
  name: nameProp,
  closeHandler,
  loadingHandler,
  successHandler,
  errorHandler,
}: Props) {
  const [name, setName] = useState('');
  const [currentUserName, setCurrentUserName] = useState('');
  const [currentUserId, setCurrentUserId] = useState(0);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);

  useEffect(() => {
    const setup = async () => {
      const me = await GetMyUser();
      if (me) {
        setCurrentUserName(me.displayName);
        setCurrentUserId(me.userID);
      }
    };
    setup();
  }, []);

  useEffect(() => {
    if (open) {
      setName(`${nameProp} (Copied from ${currentUserName})`);
      setSelectedUsers([]);
    }
  }, [open, nameProp, currentUserName]);

  const saveCallback = useCallback(async () => {
    loadingHandler?.(true);
    const includesCurrentUser = selectedUsers.includes(currentUserId);
    const res = await CopySavedSearch(ssid, name, selectedUsers, errorHandler);
    if (res) {
      successHandler?.('Search Copied', includesCurrentUser);
      closeHandler();
    }
    loadingHandler?.(false);
  }, [
    ssid,
    name,
    selectedUsers,
    currentUserId,
    errorHandler,
    successHandler,
    loadingHandler,
    closeHandler,
  ]);

  const selectedUserChangeHandler = useCallback((users: NameIdObj[]) => {
    const ids = users.map((u) => u.id);
    setSelectedUsers(ids);
  }, []);

  return (
    <Dialog open={open} fullWidth>
      <DialogTitle>Copy Saved Search</DialogTitle>
      <DialogContent>
        <Stack spacing={2} paddingTop="10px">
          <RWTextFieldComponent
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <MultipleUserPicker value={selectedUsers} onSelect={selectedUserChangeHandler} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="error" onClick={closeHandler}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={selectedUsers.length === 0 || !name.trim()}
          color="success"
          onClick={saveCallback}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
